import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Truck } from "lucide-react";
import { adminOrders, adminOrderStats, adminUpdateOrder } from "../../lib/api";

const ORDER_STATUSES = ["pending", "paid", "fulfilled", "cancelled", "refunded"];
const tone = {
  pending: "text-[#9a968c] border-[#26241f]",
  paid: "text-gold border-goldline",
  fulfilled: "text-emerald-400 border-emerald-900",
  cancelled: "text-red-400 border-red-900",
  refunded: "text-blue-300 border-blue-900",
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const OrdersTab = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, paid_orders: 0, revenue: 0, pending: 0, fulfilled: 0, customers: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([adminOrders(token), adminOrderStats(token)]);
      setOrders(o.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (order, status) => {
    try {
      await adminUpdateOrder(order.id, { status, tracking: order.tracking || "" }, token);
      toast.success(`Order ${order.order_number} marked ${status}.`);
      load();
    } catch { toast.error("Update failed."); }
  };

  const setTracking = async (order) => {
    const tracking = window.prompt(`Tracking number for ${order.order_number}`, order.tracking || "");
    if (tracking === null) return;
    try {
      await adminUpdateOrder(order.id, { status: order.status, tracking }, token);
      toast.success("Tracking saved.");
      load();
    } catch { toast.error("Update failed."); }
  };

  return (
    <div data-testid="orders-tab">
      <div className="flex justify-end mb-6">
        <button onClick={load} data-testid="orders-refresh"
          className="inline-flex items-center gap-2 px-5 py-3 border border-[#26241f] text-[#cfccc4] text-xs uppercase tracking-[0.15em] hover:border-goldline hover:text-gold transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#1a1814] mb-12 border border-[#1a1814]">
        {[
          { label: "Revenue", value: money(stats.revenue) },
          { label: "Paid orders", value: stats.paid_orders },
          { label: "Pending", value: stats.pending },
          { label: "Fulfilled", value: stats.fulfilled },
          { label: "Customers", value: stats.customers },
        ].map((s) => (
          <div key={s.label} className="bg-[#050505] p-7" data-testid={`order-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="font-serif text-3xl text-gold">{s.value}</div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#9a968c] mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="border border-[#1a1814] p-16 text-center text-[#9a968c]" data-testid="no-orders-admin">No orders yet.</div>
      ) : (
        <div className="space-y-4" data-testid="orders-list-admin">
          {orders.map((o) => (
            <div key={o.id} className="border border-[#1a1814] p-6 md:p-7" data-testid={`order-row-${o.order_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-5">
                <div>
                  <p className="font-serif text-xl text-[#f4f2ec]">{o.order_number}</p>
                  <p className="text-xs text-[#6f6c64] mt-1">
                    {new Date(o.created_at).toLocaleString("en-AU")} · {o.customer_name} ·{" "}
                    <a href={`mailto:${o.customer_email}`} className="text-gold hover:underline">{o.customer_email}</a>
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl text-gold">{money(o.total)}</p>
                  <p className="text-xs text-[#6f6c64]">incl. {money(o.delivery_fee)} delivery</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#12110e]">
                <div>
                  <p className="text-[0.6rem] uppercase tracking-[0.18em] text-[#6f6c64] mb-3">Items</p>
                  <div className="space-y-2">
                    {o.items.map((it, i) => (
                      <div key={i} className="text-sm text-[#cfccc4]">
                        {it.quantity} × {it.name} <span className="text-[#6f6c64]">{money(it.price)}</span>
                        {it.scents?.length > 0 && <p className="text-[0.65rem] text-[#6f6c64]">{it.scents.join(" · ")}</p>}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[0.6rem] uppercase tracking-[0.18em] text-[#6f6c64] mb-3">Ship to</p>
                  <p className="text-sm text-[#cfccc4] leading-relaxed">
                    {o.shipping?.full_name}<br />
                    {o.shipping?.line1}{o.shipping?.line2 ? `, ${o.shipping.line2}` : ""}<br />
                    {o.shipping?.suburb} {o.shipping?.state} {o.shipping?.postcode}
                    {o.shipping?.phone && <><br />{o.shipping.phone}</>}
                  </p>
                  {o.notes && <p className="text-xs text-[#6f6c64] mt-3">Note: {o.notes}</p>}
                  {o.tracking && <p className="text-xs text-gold mt-3">Tracking: {o.tracking}</p>}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-[#12110e]">
                {ORDER_STATUSES.map((st) => (
                  <button key={st} onClick={() => setStatus(o, st)} data-testid={`order-status-${st}-${o.order_number}`}
                    className={`text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${o.status === st ? tone[st] : "text-[#4a473f] border-transparent hover:text-[#9a968c]"}`}>
                    {st}
                  </button>
                ))}
                <button onClick={() => setTracking(o)} data-testid={`order-tracking-${o.order_number}`}
                  className="ml-auto inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.15em] text-[#9a968c] hover:text-gold transition-colors">
                  <Truck size={13} /> {o.tracking ? "Edit tracking" : "Add tracking"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
